import React from 'react';
import { Search, Star, ExternalLink, Calendar, MapPin, Tag } from 'lucide-react';

const ReviewList = ({ 
  reviews, 
  loading, 
  filters, 
  onFilterChange,
  onReviewClick
}) => {
  const [searchInput, setSearchInput] = React.useState(filters.search || '');

  // Sync searchInput when filters change externally (e.g., clear all filters)
  React.useEffect(() => {
    setSearchInput(filters.search || '');
  }, [filters.search]);

  const handleSearchChange = (e) => {
    setSearchInput(e.target.value);
  };

  const handleSearchSubmit = (e) => {
    if (e.key === 'Enter') {
      onFilterChange({ search: searchInput });
    }
  };

  const handleSearchClear = () => {
    setSearchInput('');
    onFilterChange({ search: '' });
  };

  const handleTypeFilter = (type) => {
    onFilterChange({ type: filters.type === type ? '' : type });
  };

  const handleSortChange = (sort, order) => {
    onFilterChange({ sort, order });
  };

  const handleTagClick = (tag) => {
    onFilterChange({ tags: filters.tags === tag ? '' : tag });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('da-DK');
  };


  if (loading) {
    return (
      <div className="loading">
        <p>Indlæser anmeldelser...</p>
      </div>
    );
  }

  return (
    <section style={{ position: 'relative' }} aria-label="Musik anmeldelser og koncert anmeldelser">
      {/* Floating musical notes decoration - hidden on mobile */}
      <div className="floating-note" style={{
        position: 'absolute',
        top: '20px',
        right: '5%',
        opacity: '0.1',
        animation: 'musicNote 6s ease-in-out infinite',
        zIndex: 0
      }}>
        <div style={{ fontSize: '4rem' }}>♪</div>
      </div>
      <div className="floating-note" style={{
        position: 'absolute',
        top: '200px',
        left: '3%',
        opacity: '0.08',
        animation: 'musicNote 8s ease-in-out infinite 2s',
        zIndex: 0
      }}>
        <div style={{ fontSize: '3rem' }}>♫</div>
      </div>
      
      {/* Search and Filters */}
      <div className="search-filters" style={{ position: 'relative', zIndex: 1 }}>
        <div className="filters-row">
          <div className="form-group" style={{ marginBottom: 0 }}>
            <div style={{ position: 'relative' }}>
              <Search 
                size={20} 
                color="#888" 
                style={{ 
                  position: 'absolute', 
                  left: '12px', 
                  top: '50%', 
                  transform: 'translateY(-50%)' 
                }} 
              />
              <input
                type="text"
                placeholder="Søg efter titel, artist, tags... (tryk Enter for at søge)"
                className="form-control"
                style={{ paddingLeft: '44px', paddingRight: searchInput ? '40px' : '16px' }}
                value={searchInput}
                onChange={handleSearchChange}
                onKeyDown={handleSearchSubmit}
              />
              {searchInput && (
                <button
                  onClick={handleSearchClear}
                  style={{
                    position: 'absolute',
                    right: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    color: 'var(--text-muted)',
                    cursor: 'pointer',
                    padding: '4px',
                    borderRadius: '4px',
                    fontSize: '18px',
                    transition: 'color 0.2s ease'
                  }}
                  onMouseEnter={(e) => e.target.style.color = 'var(--text-primary)'}
                  onMouseLeave={(e) => e.target.style.color = 'var(--text-muted)'}
                >
                  ×
                </button>
              )}
            </div>
          </div>
          
          <div className="filter-buttons" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
            <button
              onClick={() => onFilterChange({ search: searchInput })}
              className="btn btn-primary"
              style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '6px',
                fontSize: '14px',
                padding: '10px 16px'
              }}
            >
              <Search size={16} />
              Søg
            </button>
            
            <div className="divider" style={{ 
              width: '1px', 
              height: '30px', 
              background: 'var(--border-color)',
              margin: '0 4px'
            }} />
            
            <button
              className={`btn ${filters.type === 'album' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => handleTypeFilter('album')}
            >
              Albums
            </button>
            <button
              className={`btn ${filters.type === 'koncert' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => handleTypeFilter('koncert')}
            >
              Koncerter
            </button>
          </div>
          
          <select
            className="form-control"
            style={{ width: 'auto', minWidth: '150px' }}
            value={`${filters.sort}-${filters.order}`}
            onChange={(e) => {
              const [sort, order] = e.target.value.split('-');
              handleSortChange(sort, order);
            }}
          >
            <option value="created_at-DESC">Nyeste først</option>
            <option value="created_at-ASC">Ældste først</option>
            <option value="rating-DESC">Højeste rating</option>
            <option value="rating-ASC">Laveste rating</option>
            <option value="title-ASC">Titel A-Z</option>
            <option value="artist-ASC">Artist A-Z</option>
          </select>
        </div>
        
        {/* Active Filters */}
        {(filters.search || filters.type || filters.tags) && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            marginTop: '16px',
            paddingTop: '16px',
            borderTop: '1px solid rgba(51, 51, 51, 0.5)'
          }}>
            <span style={{
              fontSize: '14px',
              color: 'var(--text-muted)',
              fontWeight: '600'
            }}>
              Aktive filtre:
            </span>
            {filters.search && (
              <span style={{
                background: 'rgba(0, 242, 254, 0.1)',
                color: '#00f2fe',
                padding: '4px 10px',
                borderRadius: '12px',
                fontSize: '12px',
                fontWeight: '600',
                border: '1px solid rgba(0, 242, 254, 0.2)'
              }}>
                Søgning: "{filters.search}"
              </span>
            )}
            {filters.type && (
              <span style={{
                background: 'rgba(255, 107, 107, 0.1)',
                color: '#ff6b6b',
                padding: '4px 10px',
                borderRadius: '12px',
                fontSize: '12px',
                fontWeight: '600',
                border: '1px solid rgba(255, 107, 107, 0.2)'
              }}>
                Type: {filters.type}
              </span>
            )}
            {filters.tags && (
              <span style={{
                background: 'var(--primary-gradient)',
                color: 'white',
                padding: '4px 10px',
                borderRadius: '12px',
                fontSize: '12px',
                fontWeight: '600'
              }}>
                Tag: {filters.tags}
              </span>
            )}
            <button
              onClick={() => onFilterChange({ search: '', type: '', tags: '' })}
              style={{
                background: 'none',
                border: '1px solid var(--border-color)',
                color: 'var(--text-muted)',
                padding: '4px 8px',
                borderRadius: '8px',
                fontSize: '12px',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.borderColor = '#ff6b6b';
                e.target.style.color = '#ff6b6b';
              }}
              onMouseLeave={(e) => {
                e.target.style.borderColor = 'var(--border-color)';
                e.target.style.color = 'var(--text-muted)';
              }}
            >
              Ryd alle
            </button>
          </div>
        )}
      </div>

      {/* Results */}
      {reviews.length === 0 ? (
        <div className="empty-state">
          <h2>Ingen Musik Anmeldelser eller Koncert Anmeldelser Fundet</h2>
          <p>
            {filters.search || filters.type 
              ? 'Prøv at justere dine søgekriterier for at finde flere musikanmeldelser og koncertanmeldelser'
              : 'Nye musik anmeldelser og koncert anmeldelser tilføjes løbende. Kom tilbage snart for at læse de seneste anmeldelser!'
            }
          </p>
        </div>
      ) : (
        <div>
          <div style={{ 
            marginBottom: '32px', 
            color: 'var(--text-secondary)', 
            fontSize: '16px',
            fontWeight: '500'
          }}>
            {reviews.length} anmeldelse{reviews.length !== 1 ? 'r' : ''} fundet
          </div>
          
          {/* Grid Layout */}
          <div className="review-grid">
            {reviews.map((review) => (
              <div 
                key={review.id} 
                className="card clickable" 
                onClick={() => onReviewClick && onReviewClick(review)}
                style={{
                  height: 'fit-content'
                }}
              >
                {/* Image Section */}
                {review.image_url && (
                  <div style={{
                    width: '100%',
                    height: '200px',
                    borderRadius: '12px',
                    overflow: 'hidden',
                    marginBottom: '20px',
                    position: 'relative'
                  }}>
                    <img
                      src={review.image_url}
                      alt={review.title}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        transition: 'transform 0.3s ease'
                      }}
                      onMouseEnter={(e) => e.target.style.transform = 'scale(1.05)'}
                      onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
                    />
                    <div style={{
                      position: 'absolute',
                      top: '12px',
                      left: '12px',
                      display: 'flex',
                      gap: '8px'
                    }}>
                      <span className={`badge badge-${review.type}`}>
                        {review.type}
                      </span>
                      <div className="rating">
                        <Star size={14} fill="#ffd700" color="#ffd700" />
                        {review.rating}/10
                      </div>
                    </div>
                  </div>
                )}

                {/* Header without image */}
                {!review.image_url && (
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '12px',
                    marginBottom: '16px'
                  }}>
                    <span className={`badge badge-${review.type}`}>
                      {review.type}
                    </span>
                    <div className="rating">
                      <Star size={14} fill="#ffd700" color="#ffd700" />
                      {review.rating}/10
                    </div>
                  </div>
                )}
                
                {/* Title and Artist */}
                <div style={{ marginBottom: '16px' }}>
                  <h3 style={{ 
                    fontSize: '1.4rem', 
                    fontWeight: '700', 
                    marginBottom: '6px',
                    color: 'var(--text-primary)',
                    lineHeight: '1.3'
                  }}>
                    {review.title}
                  </h3>
                  
                  <p style={{ 
                    fontSize: '1.1rem', 
                    color: 'var(--text-secondary)', 
                    marginBottom: '12px',
                    fontWeight: '500'
                  }}>
                    {review.artist}
                  </p>
                  
                  {/* Date and Venue Info */}
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '12px',
                    fontSize: '13px',
                    color: 'var(--text-muted)',
                    flexWrap: 'wrap'
                  }}>
                    {review.release_date && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <Calendar size={12} />
                        {formatDate(review.release_date)}
                      </div>
                    )}
                    {review.concert_date && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <Calendar size={12} />
                        {formatDate(review.concert_date)}
                      </div>
                    )}
                    {review.venue && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <MapPin size={12} />
                        {review.venue}
                      </div>
                    )}
                  </div>
                </div>
              
                {/* Tags - Always show section, even if empty */}
                <div style={{ marginBottom: '16px' }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginBottom: '8px'
                  }}>
                    <Tag size={14} color="var(--text-muted)" />
                    <span style={{
                      fontSize: '12px',
                      color: 'var(--text-muted)',
                      textTransform: 'uppercase',
                      fontWeight: '600',
                      letterSpacing: '0.5px'
                    }}>
                      Tags
                    </span>
                  </div>
                  
                  {review.tags && review.tags.length > 0 ? (
                    <div style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '6px'
                    }}>
                      {review.tags.map((tag, tagIndex) => (
                        <span
                          key={tagIndex}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleTagClick(tag);
                          }}
                          style={{
                            background: filters.tags === tag 
                              ? 'var(--primary-gradient)' 
                              : 'rgba(102, 126, 234, 0.1)',
                            color: filters.tags === tag ? 'white' : '#667eea',
                            padding: '4px 10px',
                            borderRadius: '12px',
                            fontSize: '11px',
                            fontWeight: '600',
                            border: filters.tags === tag 
                              ? '1px solid rgba(102, 126, 234, 0.5)' 
                              : '1px solid rgba(102, 126, 234, 0.2)',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            if (filters.tags !== tag) {
                              e.target.style.background = 'rgba(102, 126, 234, 0.2)';
                            }
                            e.target.style.transform = 'translateY(-1px)';
                          }}
                          onMouseLeave={(e) => {
                            if (filters.tags !== tag) {
                              e.target.style.background = 'rgba(102, 126, 234, 0.1)';
                            }
                            e.target.style.transform = 'translateY(0)';
                          }}
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <div style={{
                      padding: '8px 12px',
                      background: 'rgba(255, 255, 255, 0.05)',
                      borderRadius: '8px',
                      fontSize: '12px',
                      color: 'var(--text-muted)',
                      fontStyle: 'italic'
                    }}>
                      Ingen tags
                    </div>
                  )}
                </div>

                {/* Review Text Preview */}
                <div style={{ 
                  marginBottom: '20px',
                  lineHeight: '1.6',
                  color: 'var(--text-secondary)'
                }}>
                  <p style={{
                    fontSize: '14px',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    position: 'relative'
                  }}>
                    {review.review_text}
                  </p>
                  {review.review_text.length > 100 && (
                    <p style={{
                      fontSize: '12px',
                      color: 'var(--text-muted)',
                      fontStyle: 'italic',
                      marginTop: '8px'
                    }}>
                      Klik for at læse hele anmeldelsen...
                    </p>
                  )}
                </div>
              
                {/* Action Links */}
                <div style={{ 
                  display: 'flex', 
                  gap: '8px',
                  flexWrap: 'wrap',
                  marginTop: 'auto'
                }}>
                  {review.listen_link && (
                    <a
                      href={review.listen_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-primary"
                      onClick={(e) => e.stopPropagation()}
                      style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '6px',
                        textDecoration: 'none',
                        fontSize: '13px',
                        padding: '10px 16px'
                      }}
                    >
                      <ExternalLink size={14} />
                      {review.type === 'album' ? 'Lyt' : 'Info'}
                    </a>
                  )}
                  {review.ticket_link && (
                    <a
                      href={review.ticket_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-secondary"
                      onClick={(e) => e.stopPropagation()}
                      style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '6px',
                        textDecoration: 'none',
                        fontSize: '13px',
                        padding: '10px 16px'
                      }}
                    >
                      <ExternalLink size={14} />
                      Billetter
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
};

export default ReviewList;
